import { useEffect, useState, type ReactNode } from "react";

import { brandLogoForLocale } from "./brand-logo";
import { brandName, locale, panelTitle, translate as t } from "./locale";
import { scheduleEditorResize } from "./editor-resize";
import { trackTelemetryAction } from "./telemetry";

export const APP_TITLE = panelTitle;
export const TRAINER_PANEL_TITLE = APP_TITLE;
export const STUDENT_PANEL_BREAKPOINT = 1100;

export const isNarrowStudentViewport = (width: number): boolean =>
    width < STUDENT_PANEL_BREAKPOINT;

export interface PanelStatus {
    label: string;
    state: "error" | "loading" | "ready";
    value: string;
}

export const PanelHeader = ({ subtitle, title }: { subtitle?: string; title: string }) => (
    <header>
        <img
            alt={brandName}
            className={`brand-logo${locale === "pl" ? "" : " brand-logo-en"}`}
            src={brandLogoForLocale(locale)}
        />
        <h1 className="eyebrow" id="assistant-title">{title}</h1>
        {subtitle && <p className="panel-account-label">{subtitle}</p>}
    </header>
);

export const StatusGrid = ({ items }: { items: PanelStatus[] }) => (
    <dl className="status-grid">
        {items.map((item) => (
            <div key={item.label}>
                <dt>{item.label}</dt>
                <dd data-state={item.state}>{item.value}</dd>
            </div>
        ))}
    </dl>
);

export const EditorShell = ({
    children,
    collapsible = false,
    panel,
}: {
    children: ReactNode;
    collapsible?: boolean;
    panel: ReactNode;
}) => {
    const [collapsed, setCollapsed] = useState(
        () => collapsible && isNarrowStudentViewport(window.innerWidth),
    );
    useEffect(() => {
        if (!collapsible) return;
        let wasNarrow = isNarrowStudentViewport(window.innerWidth);
        if (wasNarrow)
            trackTelemetryAction("panel_toggle", { state: "collapsed", trigger: "viewport" });
        const onResize = () => {
            const isNarrow = isNarrowStudentViewport(window.innerWidth);
            if (isNarrow && !wasNarrow) {
                setCollapsed(true);
                trackTelemetryAction("panel_toggle", { state: "collapsed", trigger: "viewport" });
            }
            wasNarrow = isNarrow;
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [collapsible]);
    useEffect(() => scheduleEditorResize(), [collapsed]);
    return (
        <main
            className="workspace-shell app-shell"
            data-panel-collapsed={collapsible && collapsed ? "true" : "false"}
            data-panel-kind={collapsible ? "student" : "trainer"}
            data-sidebar-collapsed={collapsible && collapsed ? "true" : "false"}
        >
            <section className="editor-pane" aria-label={t("Edytor Scratch")}>
                {children}
            </section>
            <aside className="assistant-pane" aria-labelledby="assistant-title">
                {collapsible && (
                    <button
                        aria-expanded={!collapsed}
                        aria-label={
                            collapsed ? t("Pokaż prawy panel") : t("Ukryj prawy panel")
                        }
                        className="panel-toggle"
                        onClick={() => setCollapsed((value) => {
                            const next = !value;
                            trackTelemetryAction("panel_toggle", {
                                state: next ? "collapsed" : "expanded",
                                trigger: "manual",
                            });
                            return next;
                        })}
                        title={
                            collapsed ? t("Pokaż prawy panel") : t("Ukryj prawy panel")
                        }
                        type="button"
                    >
                        <span aria-hidden="true">{">"}</span>
                    </button>
                )}
                <div className="assistant-content" hidden={collapsed}>
                    {panel}
                </div>
            </aside>
        </main>
    );
};
